import { useState, useEffect, useCallback, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Label } from '../components/ui/label';
import { Slider } from '../components/ui/slider';
import { Archive, Download, Trash2, Eye, Clock, CheckCircle, XCircle, Loader2, Layers, Link, FileText } from 'lucide-react';
import Navbar from '../components/Navbar';
import { toast } from 'sonner';

const API = '/api';

function StatusBadge({ status }) {
  const map = {
    pending: { cls: 'bg-zinc-100 text-zinc-600 border-zinc-200', icon: Clock, label: 'Pending' },
    queued: { cls: 'bg-amber-50 text-amber-700 border-amber-200', icon: Clock, label: 'Queued' },
    processing: { cls: 'bg-blue-50 text-blue-700 border-blue-200', icon: Loader2, label: 'Processing' },
    completed: { cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: CheckCircle, label: 'Completed' },
    failed: { cls: 'bg-rose-50 text-rose-700 border-rose-200', icon: XCircle, label: 'Failed' },
  };
  const s = map[status] || map.pending;
  const Icon = s.icon;
  return (
    <Badge variant="outline" className={`gap-1 text-xs font-medium border ${s.cls}`}>
      <Icon className={`w-3 h-3 ${status === 'processing' ? 'animate-spin' : ''}`} />
      {s.label}
    </Badge>
  );
}

function formatDate(str) {
  if (!str) return '-';
  return new Date(str).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function formatSize(bytes) {
  if (!bytes) return '-';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

export default function DashboardPage() {
  const [url, setUrl] = useState('');
  const [batchUrls, setBatchUrls] = useState('');
  const [depth, setDepth] = useState(3);
  const [extractionMode, setExtractionMode] = useState('normal');  // 'normal' (40 pages, fast) or 'lite' (20 pages, gentle)
  const [jobs, setJobs] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [mode, setMode] = useState('single');
  const [logsJobId, setLogsJobId] = useState(null);
  const [logs, setLogs] = useState([]);
  const navigate = useNavigate();

  const fetchJobs = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/jobs`, { withCredentials: true });
      setJobs(data);
    } catch (err) {
      console.error('Failed to fetch jobs', err);
    }
  }, []);

  useEffect(() => {
    fetchJobs();
    const iv = setInterval(fetchJobs, 10000);
    return () => clearInterval(iv);
  }, [fetchJobs]);
  // Fetch logs for a specific job
  const fetchLogs = useCallback(async (jobId) => {
    try {
      const { data } = await axios.get(`${API}/jobs/${jobId}/logs`);
      setLogs(data.logs || []);
    } catch { setLogs([]); }
  }, []);

  // Auto-refresh logs when panel is open
  useEffect(() => {
    if (!logsJobId) return;
    fetchLogs(logsJobId);
    const job = jobs.find(j => j.id === logsJobId);
    if (job && (job.status === 'processing' || job.status === 'queued')) {
      const iv = setInterval(() => fetchLogs(logsJobId), 3000);
      return () => clearInterval(iv);
    }
  }, [logsJobId, jobs, fetchLogs]);

  const toggleLogs = (jobId) => {
    if (logsJobId === jobId) { setLogsJobId(null); setLogs([]); }
    else { setLogsJobId(jobId); }
  };


  const handleSingleSubmit = async (e) => {
    e.preventDefault();
    if (!url.trim()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/jobs`, { wayback_url: url, depth, mode: extractionMode }, { withCredentials: true });
      setUrl('');
      toast.success('Extraction started');
      fetchJobs();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Failed to start extraction');
    } finally {
      setSubmitting(false);
    }
  };

  const handleBatchSubmit = async (e) => {
    e.preventDefault();
    const urls = batchUrls.split('\n').map(u => u.trim()).filter(Boolean);
    if (urls.length === 0) return;
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/jobs/batch`, { wayback_urls: urls, depth, mode: extractionMode }, { withCredentials: true });
      const created = data.created?.length || 0;
      const errors = data.errors?.length || 0;
      setBatchUrls('');
      if (created > 0) toast.success(`${created} extraction(s) started`);
      if (errors > 0) toast.error(`${errors} URL(s) had errors`);
      fetchJobs();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Failed to start batch extraction');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (jobId) => {
    try {
      await axios.delete(`${API}/jobs/${jobId}`, { withCredentials: true });
      toast.success('Deleted');
      fetchJobs();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const handleDownload = (job) => {
    const token = localStorage.getItem('access_token');
    const url = `${API}/jobs/${job.id}/download?token=${encodeURIComponent(token || '')}`;
    window.open(url, '_blank');
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="dashboard-page">
      <Navbar />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {/* URL Input */}
        <div className="border border-zinc-200 bg-white p-5 sm:p-6 mb-6" data-testid="url-input-section">
          <h2 className="text-lg font-bold tracking-tight font-heading text-zinc-950 mb-1">Extract Website</h2>
          <p className="text-sm text-zinc-500 font-body mb-4">
            Paste Wayback Machine URL(s) to download clean copies without archive overlays
          </p>

          <Tabs value={mode} onValueChange={setMode} className="mb-4">
            <TabsList className="bg-zinc-100 h-9">
              <TabsTrigger value="single" className="text-xs gap-1.5 data-[state=active]:bg-white" data-testid="tab-single">
                <Link className="w-3.5 h-3.5" /> Single URL
              </TabsTrigger>
              <TabsTrigger value="batch" className="text-xs gap-1.5 data-[state=active]:bg-white" data-testid="tab-batch">
                <Layers className="w-3.5 h-3.5" /> Batch
              </TabsTrigger>
            </TabsList>

            <TabsContent value="single" className="mt-3">
              <form onSubmit={handleSingleSubmit} className="flex flex-col sm:flex-row gap-3" data-testid="url-input-form">
                <Input
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://web.archive.org/web/20210823214658/https://example.com/"
                  className="flex-1 font-mono text-sm border-zinc-300 focus:ring-2 focus:ring-blue-600 rounded-sm"
                  data-testid="url-input"
                />
                <Button
                  type="submit"
                  disabled={submitting || !url.trim()}
                  className="bg-blue-600 hover:bg-blue-700 text-white rounded-sm px-6 h-10 whitespace-nowrap"
                  data-testid="extract-button"
                >
                  {submitting ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Extracting...</>
                  ) : (
                    <><Archive className="w-4 h-4 mr-2" /> Extract</>
                  )}
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="batch" className="mt-3">
              <form onSubmit={handleBatchSubmit} className="space-y-3" data-testid="batch-input-form">
                <textarea
                  value={batchUrls}
                  onChange={(e) => setBatchUrls(e.target.value)}
                  placeholder={"https://web.archive.org/web/20210823214658/https://site1.com/\nhttps://web.archive.org/web/20200101000000/https://site2.com/\nhttps://web.archive.org/web/20190515120000/https://site3.com/"}
                  className="w-full h-32 font-mono text-sm border border-zinc-300 rounded-sm p-3 focus:ring-2 focus:ring-blue-600 focus:outline-none resize-y bg-white"
                  data-testid="batch-url-input"
                />
                <div className="flex items-center justify-between">
                  <span className="text-xs text-zinc-400 font-body">
                    {batchUrls.split('\n').filter(u => u.trim()).length} URL(s)
                  </span>
                  <Button
                    type="submit"
                    disabled={submitting || !batchUrls.trim()}
                    className="bg-blue-600 hover:bg-blue-700 text-white rounded-sm px-6 h-10 whitespace-nowrap"
                    data-testid="batch-extract-button"
                  >
                    {submitting ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Extracting...</>
                    ) : (
                      <><Layers className="w-4 h-4 mr-2" /> Extract All</>
                    )}
                  </Button>
                </div>
              </form>
            </TabsContent>
          </Tabs>

          {/* Depth Slider & Mode Toggle */}
          <div className="flex flex-wrap items-center gap-4 pt-2 border-t border-zinc-100">
            <Label className="text-xs text-zinc-500 font-body whitespace-nowrap">Crawl depth:</Label>
            <Slider
              value={[depth]}
              onValueChange={(v) => setDepth(v[0])}
              min={1}
              max={10}
              step={1}
              className="flex-1 max-w-[200px]"
              data-testid="depth-slider"
            />
            <span className="text-sm font-mono text-zinc-700 w-6 text-center" data-testid="depth-value">{depth}</span>

            <div className="flex items-center gap-2 ml-auto">
              <Label className="text-xs text-zinc-500 font-body whitespace-nowrap">Mode:</Label>
              <div className="inline-flex rounded-md border border-zinc-200 overflow-hidden" data-testid="mode-toggle">
                <button
                  type="button"
                  onClick={() => setExtractionMode('normal')}
                  className={`px-3 py-1 text-xs font-mono transition-colors ${extractionMode === 'normal' ? 'bg-zinc-900 text-white' : 'bg-white text-zinc-600 hover:bg-zinc-50'}`}
                  data-testid="mode-normal-btn"
                  title="40 pages, 5 parallel asset workers — fastest"
                >
                  Normal
                </button>
                <button
                  type="button"
                  onClick={() => setExtractionMode('lite')}
                  className={`px-3 py-1 text-xs font-mono transition-colors border-l border-zinc-200 ${extractionMode === 'lite' ? 'bg-amber-500 text-white' : 'bg-white text-zinc-600 hover:bg-zinc-50'}`}
                  data-testid="mode-lite-btn"
                  title="20 pages, 1 worker, slow delays — use when IP is rate-limited"
                >
                  Lite (slow, stable)
                </button>
              </div>
            </div>
          </div>
          {extractionMode === 'lite' && (
            <div className="px-1 pt-1 text-[11px] font-mono text-amber-600" data-testid="lite-mode-hint">
              Lite mode: up to 20 pages, 1 asset at a time + 1s pauses. Use when the server's IP is being rate-limited by Wayback (typical sign: many failed/errors in logs).
            </div>
          )}
        </div>

        {/* Jobs List */}
        <div className="border border-zinc-200 bg-white" data-testid="jobs-list">
          <div className="px-5 py-3 border-b border-zinc-200">
            <h3 className="text-base font-bold tracking-tight font-heading text-zinc-950">Extraction History</h3>
          </div>

          {jobs.length === 0 ? (
            <div className="p-16 text-center" data-testid="empty-state">
              <img
                src="https://images.pexels.com/photos/3137073/pexels-photo-3137073.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt=""
                className="w-28 h-28 mx-auto mb-4 object-cover opacity-40 grayscale rounded-sm"
              />
              <p className="text-zinc-400 text-sm font-body">No extractions yet. Paste a URL above to get started.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="jobs-table">
                <thead>
                  <tr className="bg-zinc-50 border-b border-zinc-200 text-left">
                    <th className="px-4 py-2.5 font-medium text-zinc-600 text-xs uppercase tracking-wider">URL</th>
                    <th className="px-4 py-2.5 font-medium text-zinc-600 text-xs uppercase tracking-wider">Status</th>
                    <th className="px-4 py-2.5 font-medium text-zinc-600 text-xs uppercase tracking-wider">Progress</th>
                    <th className="px-4 py-2.5 font-medium text-zinc-600 text-xs uppercase tracking-wider">Depth</th>
                    <th className="px-4 py-2.5 font-medium text-zinc-600 text-xs uppercase tracking-wider">Files</th>
                    <th className="px-4 py-2.5 font-medium text-zinc-600 text-xs uppercase tracking-wider">Size</th>
                    <th className="px-4 py-2.5 font-medium text-zinc-600 text-xs uppercase tracking-wider">Date</th>
                    <th className="px-4 py-2.5 font-medium text-zinc-600 text-xs uppercase tracking-wider text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {jobs.map((job, i) => (
                    <Fragment key={job.id}>
                    <tr
                      className={`border-b border-zinc-100 hover:bg-zinc-50 transition-colors ${i % 2 === 1 ? 'bg-zinc-50/50' : ''}`}
                      data-testid={`job-row-${job.id}`}
                    >
                      <td className="px-4 py-3 font-mono text-xs max-w-[220px] truncate text-zinc-700">
                        {job.original_url}
                      </td>
                      <td className="px-4 py-3"><StatusBadge status={job.status} /></td>
                      <td className="px-4 py-3 w-28">
                        {job.status === 'processing' ? (
                          <div className="flex items-center gap-2">
                            <Progress value={job.progress} className="h-1.5 flex-1" />
                            <span className="text-xs text-zinc-400">{job.progress}%</span>
                          </div>
                        ) : job.status === 'completed' ? (
                          <span className="text-xs text-emerald-600">100%</span>
                        ) : job.status === 'failed' ? (
                          <span
                            className="text-xs text-rose-500 cursor-help underline decoration-dotted"
                            title={job.error || 'Job failed — open logs for details'}
                            data-testid={`error-cell-${job.id}`}
                          >
                            Error
                          </span>
                        ) : (
                          <span className="text-xs text-zinc-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs text-zinc-600 font-mono">{job.depth || '-'}</td>
                      <td className="px-4 py-3 text-xs text-zinc-600">{job.total_files || '-'}</td>
                      <td className="px-4 py-3 text-xs text-zinc-600">{formatSize(job.zip_size)}</td>
                      <td className="px-4 py-3 text-xs text-zinc-500">{formatDate(job.created_at)}</td>
                      <td className="px-4 py-3">
                        <div className="flex gap-1 justify-end">
                          {/* Logs button available for ALL job states so users can debug failures */}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => toggleLogs(job.id)}
                            className={`h-8 w-8 p-0 ${logsJobId === job.id ? 'text-blue-600 bg-blue-50' : ''} ${job.status === 'failed' ? 'text-rose-500' : ''}`}
                            data-testid={`logs-job-${job.id}`}
                            title={job.status === 'failed' ? 'View logs (see why it failed)' : 'View logs'}
                          >
                            <FileText className="w-4 h-4" />
                          </Button>
                          {job.status === 'completed' && (
                            <>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => navigate(`/jobs/${job.id}`)}
                                className="h-8 w-8 p-0"
                                data-testid={`view-job-${job.id}`}
                                title="Preview files"
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleDownload(job)}
                                className="h-8 w-8 p-0"
                                data-testid={`download-job-${job.id}`}
                                title="Download ZIP"
                              >
                                <Download className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDelete(job.id)}
                            className="h-8 w-8 p-0 text-zinc-400 hover:text-rose-600"
                            data-testid={`delete-job-${job.id}`}
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                    {logsJobId === job.id && (
                      <tr>
                        <td colSpan="8" className="px-4 py-3 bg-zinc-900">
                          <div className="font-mono text-xs text-zinc-300 max-h-48 overflow-y-auto space-y-0.5" data-testid={`logs-panel-${job.id}`}>
                            {logs.length === 0 ? (
                              <div className="text-zinc-500">No logs yet...</div>
                            ) : logs.map((line, i) => (
                              <div key={i} className={`${line.includes('warning') || line.includes('Timeout') ? 'text-amber-400' : line.includes('error') || line.includes('Error') ? 'text-rose-400' : line.includes('Done') ? 'text-emerald-400' : 'text-zinc-300'}`}>
                                {line}
                              </div>
                            ))}
                          </div>
                        </td>
                      </tr>
                    )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
